import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Upload, FileText, CheckCircle2, Circle, Trash2, X } from "lucide-react";

const CHECKBOX_FIELDS = [
    { key: "worksheet_delivered", label: "Worksheet" },
    { key: "quiz_delivered", label: "Quiz" },
    { key: "test_delivered", label: "Test" },
];

export default function TeacherPlans() {
    const [plans, setPlans] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [parsedPreview, setParsedPreview] = useState(null);
    const [form, setForm] = useState({ subject_id: "", class_id: "", period: "Janar - Shkurt - Mars", year: "2025-2026", field: "", themes: [], units: [], methodology: [], evaluation_methods: [], resources: [], source_file_id: null });
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const backPath = useBackHomePath();

    const refresh = async () => {
        setLoading(true);
        try {
            const [p, s, c] = await Promise.all([
                api.get("/teacher/quarterly-plans"),
                api.get("/teacher/my-subjects"),
                api.get("/classes"),
            ]);
            setPlans(p.data); setSubjects(s.data); setClasses(c.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const onDocxPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setErr("");
        try {
            const fd = new FormData(); fd.append("file", file);
            const r = await api.post("/teacher/quarterly-plan/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const parsed = r.data;
            setParsedPreview(parsed);
            setForm({
                subject_id: parsed.subject_id_hint || "",
                class_id: parsed.class_id_hint || "",
                period: parsed.period || "",
                year: parsed.year || "",
                field: parsed.field || "",
                themes: parsed.themes || [],
                units: parsed.units || [],
                methodology: parsed.methodology || [],
                evaluation_methods: parsed.evaluation_methods || [],
                resources: parsed.resources || [],
                source_file_id: parsed.source_file_id || null,
            });
            setOpen(true);
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/teacher/quarterly-plan", form);
            setOpen(false); setParsedPreview(null);
            setForm({ subject_id: "", class_id: "", period: "Janar - Shkurt - Mars", year: "2025-2026", field: "", themes: [], units: [], methodology: [], evaluation_methods: [], resources: [], source_file_id: null });
            refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const addManualUnit = () => setForm((s) => ({ ...s, units: [...s.units, { index: (s.units.length + 1), name: "", hours: 1, worksheet_delivered: false, quiz_delivered: false, test_delivered: false }] }));
    const removeUnit = (i) => setForm((s) => ({ ...s, units: s.units.filter((_, idx) => idx !== i) }));
    const updateUnit = (i, k, v) => setForm((s) => ({ ...s, units: s.units.map((u, idx) => idx === i ? { ...u, [k]: v } : u) }));

    const togglePlanUnit = async (planId, unit, field) => {
        const newVal = !unit[field];
        const patch = { [field]: newVal };
        if (newVal && field !== "delivered_date") patch.delivered_date = new Date().toISOString().slice(0, 10);
        try {
            await api.patch(`/teacher/quarterly-plan/${planId}/unit/${unit.index}`, patch);
            setPlans((ps) => ps.map((p) => p.id === planId ? { ...p, units: p.units.map((u) => u.index === unit.index ? { ...u, ...patch } : u) } : p));
        } catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
    };

    const deletePlan = async (pid) => {
        if (!window.confirm("Fshi këtë plan?")) return;
        await api.delete(`/teacher/quarterly-plan/${pid}`);
        refresh();
    };

    const planProgress = (p) => {
        const total = p.units.length * 3;
        if (total === 0) return 0;
        const done = p.units.reduce((acc, u) => acc + (u.worksheet_delivered ? 1 : 0) + (u.quiz_delivered ? 1 : 0) + (u.test_delivered ? 1 : 0), 0);
        return Math.round((done / total) * 100);
    };

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Planet Tremujore</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Planet e mia</h1>
                    <p className="text-slate-600 mt-2 text-sm">Ngarko nga Word ose krijo manualisht • Shëno worksheet/quiz/test për çdo njësi</p>
                </div>
                <div className="flex gap-2">
                    <label className={`inline-flex items-center gap-2 h-11 px-4 rounded-md border border-oxford bg-white hover:bg-oxford/5 text-oxford text-sm font-semibold cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="plan-upload-btn">
                        <Upload className="w-4 h-4" /> {uploading ? "Duke ngarkuar…" : "Ngarko Word"}
                        <input type="file" accept=".docx" onChange={onDocxPick} disabled={uploading} className="hidden" data-testid="plan-upload-input" />
                    </label>
                    <Button onClick={() => { setParsedPreview(null); setForm({ subject_id: "", class_id: "", period: "Janar - Shkurt - Mars", year: "2025-2026", field: "", themes: [], units: [], methodology: [], evaluation_methods: [], resources: [], source_file_id: null }); setOpen(true); }} className="bg-oxford hover:bg-oxfordhover text-white gap-2" data-testid="plan-new-btn">
                        <Plus className="w-4 h-4" /> Krijo Manual
                    </Button>
                </div>
            </div>

            {err && <div className="mb-3 text-sm text-terracotta bg-terracotta/10 border border-terracotta/30 rounded-lg px-4 py-2">{err}</div>}

            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : plans.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <FileText className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Pa plane ende</div>
                    <p className="text-slate-600 mt-1 text-sm">Ngarko një .docx ose krijo manualisht.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {plans.map((p) => {
                        const progress = planProgress(p);
                        const isOpen = expandedId === p.id;
                        return (
                            <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`plan-${p.id}`}>
                                <button onClick={() => setExpandedId(isOpen ? null : p.id)} className="w-full text-left px-4 sm:px-5 py-4 hover:bg-bone/30">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className="font-heading font-black text-slate-900">{p.subject_name}</span>
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-oxford/10 text-oxford font-bold">{p.class_name}</span>
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-bone text-slate-700 border border-slate-200 font-semibold">{p.period}</span>
                                        <span className="text-[11px] text-slate-500">• {p.year}</span>
                                        <span className="ml-auto text-xs text-slate-500">{p.units.length} njësi</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-sage" style={{ width: `${progress}%` }} />
                                        </div>
                                        <span className="text-xs font-bold text-slate-700">{progress}% dorëzuar</span>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="border-t border-slate-200">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-bone/40">
                                                    <tr className="text-[10px] uppercase tracking-widest text-slate-700 font-bold">
                                                        <th className="text-left px-4 py-2 w-12">#</th>
                                                        <th className="text-left px-4 py-2">Njësia</th>
                                                        <th className="text-center px-2 py-2 w-16">Orë</th>
                                                        {CHECKBOX_FIELDS.map((f) => <th key={f.key} className="text-center px-2 py-2 w-24">{f.label}</th>)}
                                                        <th className="text-center px-2 py-2 w-24">Data</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {p.units.map((u) => (
                                                        <tr key={u.index} className="border-t border-slate-100 hover:bg-bone/20" data-testid={`plan-${p.id}-unit-${u.index}`}>
                                                            <td className="px-4 py-2 text-xs font-bold text-slate-500">{u.index}</td>
                                                            <td className="px-4 py-2">{u.name}</td>
                                                            <td className="text-center text-xs text-slate-500">{u.hours}h</td>
                                                            {CHECKBOX_FIELDS.map((f) => (
                                                                <td key={f.key} className="text-center px-2 py-2">
                                                                    <button onClick={() => togglePlanUnit(p.id, u, f.key)} className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition ${u[f.key] ? "bg-sage text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`} data-testid={`plan-${p.id}-unit-${u.index}-${f.key}`}>
                                                                        {u[f.key] ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                                                                    </button>
                                                                </td>
                                                            ))}
                                                            <td className="text-center text-[10px] text-slate-500 font-mono">{u.delivered_date || "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="px-4 py-3 bg-bone/40 border-t border-slate-200 flex flex-wrap gap-2 items-center">
                                            {p.source_file_id && (
                                                <a href={`${api.defaults.baseURL}/lms/file/${p.source_file_id}?auth=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" className="text-xs text-oxford hover:underline inline-flex items-center gap-1">
                                                    <FileText className="w-3 h-3" /> Shkarko Word origjinal
                                                </a>
                                            )}
                                            <button onClick={() => deletePlan(p.id)} className="ml-auto inline-flex items-center gap-1 text-xs text-terracotta hover:bg-terracotta/10 px-2 py-1 rounded" data-testid={`plan-delete-${p.id}`}>
                                                <Trash2 className="w-3 h-3" /> Fshi planin
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* New / Upload Plan Dialog */}
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParsedPreview(null); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">{parsedPreview ? "Konfirmo Plani nga Word" : "Krijo Plan Tremujor"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        {parsedPreview && (
                            <div className="text-xs bg-sage/10 border border-sage/30 rounded-lg p-3 text-sage">
                                ✓ Skedari u parsua: {parsedPreview.units.length} njësi • {parsedPreview.themes.length} tema • {parsedPreview.subject_name || "—"}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Lënda *</label>
                                <select required value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="plan-form-subject">
                                    <option value="">— Zgjidh —</option>
                                    {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Klasa *</label>
                                <select required value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="plan-form-class">
                                    <option value="">— Zgjidh —</option>
                                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <Input placeholder="Periudha (p.sh. Janar - Shkurt - Mars)" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} required data-testid="plan-form-period" />
                            <Input placeholder="Viti (p.sh. 2025-2026)" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} required data-testid="plan-form-year" />
                        </div>
                        <Input placeholder="Fusha kurrikulare (opsional)" value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })} />
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Njësitë mësimore ({form.units.length})</label>
                                <button type="button" onClick={addManualUnit} className="text-[11px] text-oxford font-semibold hover:underline inline-flex items-center gap-1" data-testid="plan-add-unit"><Plus className="w-3 h-3" /> Shto njësi</button>
                            </div>
                            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                                {form.units.map((u, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2">
                                        <span className="text-[11px] font-bold text-slate-400 w-7 text-center">{u.index}</span>
                                        <Input value={u.name} onChange={(e) => updateUnit(i, "name", e.target.value)} placeholder="Emri i njësisë" className="flex-1 h-8 text-sm" />
                                        <Input type="number" value={u.hours} onChange={(e) => updateUnit(i, "hours", parseInt(e.target.value) || 1)} className="w-16 h-8 text-sm" />
                                        <button type="button" onClick={() => removeUnit(i)} className="text-terracotta hover:bg-terracotta/10 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ))}
                                {form.units.length === 0 && <div className="p-6 text-xs text-slate-500 text-center">Pa njësi. Klik "Shto njësi" ose ngarko nga Word.</div>}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="plan-form-submit">{busy ? "Duke ruajtur…" : "Ruaj planin"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
